import { useObserver, useSubtree } from "subtree.js/react";
import { FillProgress, SpotGrid } from "../../../../components/live/SpotGrid";
import { breakBadge, liveBadge } from "../../../../core/primitives/live";
import { LiveStreamState } from "../../live-stream-model";
import { ViewerPreviewActions, ViewerPreviewState } from "./viewer-preview-controller";

/**
 * The stream as a buyer has it, on the host's own screen.
 *
 * No video element: the picture a buyer sees is this device's camera, already
 * filling the screen behind this panel. And nothing here is clickable — it is a
 * view of the room, not a way to buy your own spots. The chat is the cockpit's
 * one conversation, shown beside it, not a second copy of it.
 */
export function ViewerPreview() {
  const page = useSubtree(LiveStreamState);
  const state = useSubtree(ViewerPreviewState);
  const actions = useSubtree(ViewerPreviewActions);
  const observer = useObserver();
  const stream = observer.watch(page.stream);
  const activeBreak = observer.watch(page.shownBreak);
  const slots = observer.watch(page.slots);
  const watching = observer.watch(page.watching);
  const poolOpen = observer.watch(state.poolOpen);

  if (!stream) return null;

  const badge = liveBadge(stream);
  const status = activeBreak ? breakBadge(activeBreak.status) : null;
  const revealed = activeBreak?.randomizedAt !== null && activeBreak?.randomizedAt !== undefined;

  return (
    <div className="space-y-3">
      <div className="panel flex flex-wrap items-center gap-2 p-3">
        <span className={`badge ${badge.cls}`}>{badge.label}</span>
        <span className="min-w-0 truncate font-semibold">{stream.title}</span>
        {stream.host && <span className="truncate text-sm opacity-60">with {stream.host.name}</span>}
        <span className="ml-auto text-sm tabular-nums opacity-60">{watching ?? 0} watching</span>
      </div>

      {activeBreak ? (
        <div className="panel space-y-3 p-3">
          <div className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate font-semibold">{activeBreak.title}</span>
            {status && <span className={`badge ${status.cls}`}>{status.label}</span>}
          </div>

          <FillProgress soldCount={activeBreak.soldCount} slotCount={activeBreak.slotCount} />

          <p className="text-sm opacity-60">
            {revealed
              ? "The draw has happened. Every spot below shows what it got."
              : "A random draw decides what each spot gets when the rip starts. Nobody, including us, knows who gets what until then."}
          </p>

          {activeBreak.labelPool.length > 0 && (
            <div>
              <button type="button" className="btn-secondary btn-sm w-full" onClick={actions.togglePool}>
                What is in the draw ({activeBreak.labelPool.length})
              </button>
              {poolOpen && <p className="mt-2 text-xs opacity-60">{activeBreak.labelPool.join(" · ")}</p>}
            </div>
          )}

          {/* The commitment is the half that makes the seed mean anything: buyers
              see the hash before the draw and the seed after, and can check one
              against the other. */}
          {activeBreak.seedCommitment && (
            <p className="note text-xs">
              {revealed
                ? "The result can be checked against the hash published before the draw."
                : "The result is locked in already."}
            </p>
          )}

          <p className="text-sm">
            <span className="font-medium">You keep the video.</span>{" "}
            <span className="opacity-60">
              Your cards get opened on camera, and the clip of the moment they were pulled is yours after the stream.
            </span>
          </p>

          {/* readOnly: the host's notes and the tap-to-mark affordance are not
              things a buyer has. */}
          <SpotGrid slots={[...slots]} readOnly priceCents={activeBreak.defaultPriceCents} />
        </div>
      ) : (
        <p className="note">Nothing is on sale, so a buyer arriving now has nothing to buy.</p>
      )}
    </div>
  );
}
